/**
 *
 * PhoneAuthenticatorCodeSection Component
 *
 */

import {
  Button,
  stylize,
  TextField,
} from "@envestnet/envreact-component-library";
import classnames from "classnames";
import React, { useEffect, useState } from "react";
import withIntl from "utils/withIntl";
import messages from "./messages";
import styles, { Styles } from "./styles";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
}

export interface PhoneAuthenticatorCodeSectionProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  cancelCallback: () => void;

  loading: boolean;

  verifyCallback: (passcode: string | undefined) => void;

  passCodeError: string | null;

  setPassCodeError: (val: string | null) => void;

  restartPhone: () => void;
}

const defaultProps: DefaultProps = {
  anotherProp: 10,
};

type Props = InjectedProps & PhoneAuthenticatorCodeSectionProps;

const PhoneAuthenticatorCodeSection = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    loading,
    cancelCallback,
    verifyCallback,
    passCodeError,
    setPassCodeError,
    restartPhone,
  } = props;

  const [code, setCode] = useState<string>();
  const [codeError, setCodeError] = useState<string | null>(null);

  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [showLink, setShowLink] = useState<boolean>(false);

  useEffect(() => {
    if (!showLink) {
      setTimeout(() => {
        setShowLink(true);
      }, 30000);
    }
  }, [showLink]);

  // TODO: Calling setPassCodeError tries to update the layout which is currently not in use. We need to fix this.
  const getError = (): string | null => {
    let error: string | null = null;
    if (passCodeError) {
      error = passCodeError;
    }
    if (codeError) {
      setPassCodeError(null);
      error = codeError;
    }

    return error;
  };

  const codeRegex = /^[0-9]+$/;

  const codeValidator = (val: string | null): boolean => {
    let hasError = false;
    if (val === "" || val === null) {
      setCodeError(formatMessage(messages.required));
      setButtonDisabled(true);
      hasError = true;
    } else if (!codeRegex.test(val)) {
      setCodeError(formatMessage(messages.numbersOnly));
      setButtonDisabled(true);
    } else {
      setButtonDisabled(false);
      setCodeError(null);
    }
    return hasError;
  };

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div className={classnames(classes.header, "header-text-font-family")}>
        {formatMessage(messages.header)}
      </div>
      <div className={classes.codeTextFieldSection}>
        <div
          className={classnames(
            classes.codeTextFieldLabel,
            "header-font-family"
          )}
        >
          {formatMessage(messages.enterCode)}
        </div>
        <TextField
          className={classnames(
            classes.codeTextField,
            "header-text-font-family",
            "no-border-radius"
          )}
          aria-label="phone authenticator code text field"
          data-testid="phone-code-textfield"
          textStyle={{ fontFamily: "Red Hat Text" }}
          text={code}
          onChangeText={(val) => {
            codeValidator(val);
            setCode(val);
          }}
          errorMessage={getError()}
        />
      </div>
      <div className={classes.buttonContainer}>
        <Button
          buttonStyle="primary"
          name={formatMessage(messages.submitButton)}
          fullWidth
          className={classnames(
            classes.verifyButton,
            "no-border-radius",
            "header-text-font-family"
          )}
          loading={loading}
          onClick={() => verifyCallback(code)}
          disabled={buttonDisabled}
          aria-label="submit code button"
        />

        <Button
          buttonStyle="link"
          noUnderline
          name={formatMessage(messages.cancelButton)}
          className={classnames(
            classes.cancelButton,
            "header-text-font-family"
          )}
          onClick={() => cancelCallback()}
          aria-label="cancel button"
        />

        {showLink && (
          <Button
            buttonStyle="link"
            noUnderline
            name={formatMessage(messages.restart)}
            className={classnames(
              classes.cancelButton,
              "header-text-font-family"
            )}
            onClick={() => restartPhone()}
            aria-label="didn't get code link"
          />
        )}
      </div>
    </div>
  );
};

PhoneAuthenticatorCodeSection.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(PhoneAuthenticatorCodeSection)
);
